import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ModalHelper from "../../../Generics/ModalHelper";
import GenericDatePicker from "../../../Generics/GenericDatePicker";
import DocumentUpload from "../../../Generics/UploadDocument";
import EmailField from "../../../Generics/EmailField";
import "../../../../assets/css/text.css";
import { service } from "../../../../model/service";

const SameAsResidence = [
  {
    key: true,
    labelIta: "Uguale alla residenza",
    labelEng: "Same as residence",
  },
  {
    key: false,
    labelIta: "Diverso dalla residenza",
    labelEng: "Other than residence",
  },
];
const PoliticallyExposed = [
  { key: true, labelIta: "Sì", labelEng: "Yes" },
  { key: false, labelIta: "No", labelEng: "No" },
];
const DocumentTypes = [
  {
    key: "Carta d'identità",
    labelIta: "Carta d'identità",
    labelEng: "ID",
  },
  { key: "Passaporto", labelIta: "Passaporto", labelEng: "Passport" },
  {
    key: "Patente di guida",
    labelIta: "Patente di guida",
    labelEng: "Driving license",
  },
];

const pt1 = { paddingTop: { xs: 2, md: 0 } };
const pt2 = { paddingTop: { xs: 2, md: 2, lg: 0 } };
const pt3 = { paddingTop: 2 };

const setNested = (obj, path, value) => {
  const keys = path.split(".");
  if (keys.length === 1) return { ...obj, [keys[0]]: value };
  return {
    ...obj,
    [keys[0]]: setNested(obj[keys[0]] || {}, keys.slice(1).join("."), value),
  };
};

const TitolareAmlGiuridico = ({
  languange,
  errors,
  existingData,
  indexTitolare,
  setSingleTitolare,
  tooltipContent,
  saveAml,
  documents,
  refreshDocuments,
  saveAmlData,
  beneficialOwnersCount,
}) => {
  const [data, setData] = useState(existingData || {});

  const findDocument = (name) =>
    (documents || []).find((d) => {
      const base =
        d.assigned_name?.split(".")[0] ?? d.original_name?.split(".")[0];
      return base === name;
    }) ?? null;

  const idx = indexTitolare !== null ? indexTitolare : beneficialOwnersCount;

  const DOCUMENT_FILES_MAP = {
    "Carta d'identità": [
      `titolare_${idx}_carta_identita_fronte`,
      `titolare_${idx}_carta_identita_retro`,
      `beneficial_owner_${idx}_identity_card_front`,
      `beneficial_owner_${idx}_identity_card_back`,
    ],
    "Patente di guida": [
      `titolare_${idx}_patente`,
      `beneficial_owner_${idx}_driving_license`,
    ],
    Passaporto: [
      `titolare_${idx}_passaporto`,
      `beneficial_owner_${idx}_passport`,
    ],
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [modalObjectInfo, setModalObjectInfo] = useState({});

  useEffect(() => {
    setData(existingData || {});
  }, [indexTitolare]);

  useEffect(() => {
    if (typeof setSingleTitolare === "function") {
      setSingleTitolare(data);
    }
  }, [data]);

  const t = (eng, ita) => (languange === "ENG" ? eng : ita);
  const ph = t("Insert", "Inserisci");

  const handleDocumentTypeChange = async (e) => {
    const newType = e.target.value;
    const previousType = getValue("document.type");
    if (previousType && previousType !== newType) {
      const previousFileNames = DOCUMENT_FILES_MAP[previousType] || [];
      const docsToDelete = (documents || []).filter((d) => {
        const base =
          d.assigned_name?.split(".")[0] ?? d.original_name?.split(".")[0];
        return previousFileNames.includes(base);
      });
      for (const doc of docsToDelete) {
        try {
          await service.deleteDocument(doc.id);
        } catch (err) {
          console.error("Errore eliminazione documento:", err);
        }
      }
      if (typeof refreshDocuments === "function") await refreshDocuments();
    }
    onChange(e);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => setNested(prev, name, value));
  };

  const getValue = (path) =>
    path.split(".").reduce((obj, key) => obj?.[key], data) ?? "";

  const field = (name) => ({
    name,
    value: getValue(name),
    error: errors.includes(name),
    onChange,
    fullWidth: true,
    placeholder: ph,
    style: {},
  });

  const openModal = (info) => {
    setModalObjectInfo(info);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalObjectInfo({});
  };

  const showDomicilioSection = data.domicile_same === false;
  const showPepQualita = data.is_pep === true;

  return (
    <>
      <Grid item xs={11}>
        <Typography className="color-blue-dark title-sezione-step">
          {t("Identification data and role", "Dati anagrafici e ruolo")}
        </Typography>
      </Grid>

      <Grid
        xs={12}
        columnSpacing={4}
        rowSpacing={0}
        item
        container
        style={{ display: "flex", justifyContent: "center" }}
      >
        {/* ── DATI ANAGRAFICI ── */}
        <Grid item xs={11} md={6} lg={4}>
          <Typography className="color-blue-dark title-input">
            {t("Name", "Nome")}
          </Typography>
          <TextField {...field("first_name")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt1}>
          <Typography className="color-blue-dark title-input">
            {t("Lastname", "Cognome")}
          </Typography>
          <TextField {...field("last_name")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt2}>
          <Typography className="color-blue-dark title-input">
            {t("Place of birth", "Luogo di nascita")}
          </Typography>
          <TextField {...field("birth_place")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt3}>
          <Typography className="color-blue-dark title-input">
            {t("Date of birth", "Data di nascita")}
          </Typography>
          <GenericDatePicker
            errors={errors}
            languange={languange}
            name="birth_date"
            value={getValue("birth_date")}
            handleChange={onChange}
          />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt3}>
          <Typography className="color-blue-dark title-input">
            {t("Tax ID number", "Codice fiscale")}
          </Typography>
          <TextField {...field("tax_code")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt3}>
          <Typography className="color-blue-dark title-input">
            {t("Occupation/Activity", "Professione/Attività")}
          </Typography>
          <TextField {...field("profession")} variant="outlined" />
        </Grid>

        <Grid xs={11} md={12} item>
          <Divider className="divider" />
        </Grid>

        {/* ── RESIDENZA ── */}
        <Grid xs={11} md={12} item container>
          <Grid item xs={11} className="container-title-sezione-step">
            <Typography className="color-blue-dark title-sezione-step">
              {t("Residence", "Residenza")}
            </Typography>
          </Grid>
        </Grid>

        <Grid item xs={11} md={6} lg={4}>
          <Typography className="color-blue-dark title-input">
            {t("Address", "Indirizzo")}
          </Typography>
          <TextField {...field("residence.address")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt1}>
          <Typography className="color-blue-dark title-input">
            {t("ZIP code", "CAP")}
          </Typography>
          <TextField {...field("residence.zip")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt2}>
          <Typography className="color-blue-dark title-input">
            {t("City", "Città")}
          </Typography>
          <TextField {...field("residence.city")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt3}>
          <Typography className="color-blue-dark title-input">
            {t("Province", "Provincia")}
          </Typography>
          <TextField {...field("residence.province")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt3}>
          <Typography className="color-blue-dark title-input">
            {t("Nation", "Nazione")}
          </Typography>
          <TextField {...field("residence.country")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt3}>
          <Typography className="color-blue-dark title-input">
            {t("Domicile", "Domicilio")}
          </Typography>
          <Select
            name="domicile_same"
            value={
              data.domicile_same === "" || data.domicile_same == null
                ? ""
                : String(data.domicile_same)
            }
            error={errors.includes("domicile_same")}
            onChange={(e) =>
              setData((prev) => ({
                ...prev,
                domicile_same:
                  e.target.value === "true"
                    ? true
                    : e.target.value === "false"
                      ? false
                      : "",
              }))
            }
            fullWidth
            displayEmpty
          >
            <MenuItem value="">
              <span style={{ color: "#abb0b6" }}>Seleziona</span>
            </MenuItem>
            {SameAsResidence.map((item, i) => (
              <MenuItem key={i} value={String(item.key)}>
                {t(item.labelEng, item.labelIta)}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid xs={11} md={12} item>
          <Divider className="divider" />
        </Grid>

        {/* ── DOMICILIO ── */}
        {showDomicilioSection && (
          <>
            <Grid xs={11} md={12} item container>
              <Grid item xs={11} className="container-title-sezione-step">
                <Typography className="color-blue-dark title-sezione-step">
                  {t("Domicile", "Domicilio")}
                </Typography>
              </Grid>
            </Grid>
            <Grid item xs={11} md={6} lg={4}>
              <Typography className="color-blue-dark title-input">
                {t("Address", "Indirizzo")}
              </Typography>
              <TextField {...field("domicile.address")} variant="outlined" />
            </Grid>
            <Grid item xs={11} md={6} lg={4} sx={pt1}>
              <Typography className="color-blue-dark title-input">
                {t("ZIP code", "CAP")}
              </Typography>
              <TextField {...field("domicile.zip")} variant="outlined" />
            </Grid>
            <Grid item xs={11} md={6} lg={4} sx={pt2}>
              <Typography className="color-blue-dark title-input">
                {t("City", "Città")}
              </Typography>
              <TextField {...field("domicile.city")} variant="outlined" />
            </Grid>
            <Grid item xs={11} md={6} lg={4} sx={pt3}>
              <Typography className="color-blue-dark title-input">
                {t("Province", "Provincia")}
              </Typography>
              <TextField {...field("domicile.province")} variant="outlined" />
            </Grid>
            <Grid item xs={11} md={6} lg={4} sx={pt3}>
              <Typography className="color-blue-dark title-input">
                {t("Nation", "Nazione")}
              </Typography>
              <TextField {...field("domicile.country")} variant="outlined" />
            </Grid>
            <Grid item xs={11} md={6} lg={4} sx={pt3} />
            <Grid xs={11} md={12} item>
              <Divider className="divider" />
            </Grid>
          </>
        )}

        {/* ── CONTATTI ── */}
        <Grid xs={11} md={12} item container>
          <Grid item xs={11} className="container-title-sezione-step">
            <Typography className="color-blue-dark title-sezione-step">
              {t(
                "Contact details for electronic correspondence",
                "Recapiti per la corrispondenza elettronica",
              )}
            </Typography>
          </Grid>
        </Grid>

        <Grid item xs={11} md={6} lg={4}>
          <EmailField
            label={t("PEC address", "Indirizzo PEC")}
            name="pec"
            value={getValue("pec")}
            onChange={onChange}
            placeholder={ph}
            errors={errors}
          />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt1}>
          <EmailField
            label={t("E-mail address", "Indirizzo e-mail")}
            name="email"
            value={getValue("email")}
            onChange={onChange}
            placeholder={ph}
            errors={errors}
          />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt2} />
        <Grid xs={11} md={12} item>
          <Divider className="divider" />
        </Grid>

        {/* ── DOCUMENTO ── */}
        <Grid xs={11} md={12} item container>
          <Grid item xs={11} className="container-title-sezione-step">
            <Typography className="color-blue-dark title-sezione-step">
              {t(
                "Data of the identification document",
                "Estremi del documento di identificazione",
              )}
            </Typography>
          </Grid>
        </Grid>

        <Grid item xs={11} md={6} lg={4}>
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <Typography className="color-blue-dark title-input">
              {t("Document type", "Tipo documento")}
            </Typography>
          </div>
          <Select
            name="document.type"
            value={getValue("document.type")}
            error={errors.includes("document.type")}
            onChange={handleDocumentTypeChange}
            fullWidth
            displayEmpty
          >
            <MenuItem value="">
              <span style={{ color: "#abb0b6" }}>
                {t("Select", "Seleziona")}
              </span>
            </MenuItem>
            {DocumentTypes.map((item, i) => (
              <MenuItem key={i} value={item.key}>
                {t(item.labelEng, item.labelIta)}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt1}>
          <Typography className="color-blue-dark title-input">
            {t("Document number", "Numero documento")}
          </Typography>
          <TextField {...field("document.number")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt2}>
          <Typography className="color-blue-dark title-input">
            {t("Issuing body", "Ente rilascio")}
          </Typography>
          <TextField {...field("document.issuer")} variant="outlined" />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt3}>
          <Typography className="color-blue-dark title-input">
            {t("Issuing date", "Data rilascio")}
          </Typography>
          <GenericDatePicker
            errors={errors}
            languange={languange}
            name="document.issue_date"
            value={getValue("document.issue_date")}
            handleChange={onChange}
          />
        </Grid>

        <Grid item xs={11} md={6} lg={4} sx={pt3}>
          <Typography className="color-blue-dark title-input">
            {t("Expiration date", "Data scadenza")}
          </Typography>
          <GenericDatePicker
            errors={errors}
            languange={languange}
            name="document.expiry_date"
            value={getValue("document.expiry_date")}
            handleChange={onChange}
          />
        </Grid>

        {data.document?.type ? (
          data.document.type === "Carta d'identità" ? (
            <>
              <Grid item xs={11} md={6} lg={4} sx={pt3}>
                <DocumentUpload
                  languange={languange}
                  label={t("Document front", "Fronte documento identità")}
                  noGrid
                  isIdentityDocument={true}
                  documentSide={t(
                    `beneficial_owner_${idx}_identity_card_front`,
                    `titolare_${idx}_carta_identita_fronte`,
                  )}
                  existingDocument={findDocument(
                    t(
                      `beneficial_owner_${idx}_identity_card_front`,
                      `titolare_${idx}_carta_identita_fronte`,
                    ),
                  )}
                  refreshDocuments={refreshDocuments}
                  saveAmlData={saveAmlData}
                  hasError={errors.includes("document.upload_front")}
                />
              </Grid>
              <Grid item xs={11} md={6} lg={4} sx={pt3}>
                <DocumentUpload
                  languange={languange}
                  label={t("Document back", "Retro documento identità")}
                  noGrid
                  isIdentityDocument={true}
                  documentSide={t(
                    `beneficial_owner_${idx}_identity_card_back`,
                    `titolare_${idx}_carta_identita_retro`,
                  )}
                  existingDocument={findDocument(
                    t(
                      `beneficial_owner_${idx}_identity_card_back`,
                      `titolare_${idx}_carta_identita_retro`,
                    ),
                  )}
                  refreshDocuments={refreshDocuments}
                  saveAmlData={saveAmlData}
                  hasError={errors.includes("document.upload_back")}
                />
              </Grid>
              <Grid item xs={11} md={6} lg={4} />
              <Grid item xs={11} md={6} lg={4} />
            </>
          ) : data.document.type === "Patente di guida" ? (
            <>
              <Grid item xs={11} md={6} lg={4} sx={pt3}>
                <DocumentUpload
                  languange={languange}
                  label={t("Upload document", "Carica documento")}
                  noGrid
                  isIdentityDocument={true}
                  documentSide={t(
                    `beneficial_owner_${idx}_driving_license`,
                    `titolare_${idx}_patente`,
                  )}
                  existingDocument={findDocument(
                    t(
                      `beneficial_owner_${idx}_driving_license`,
                      `titolare_${idx}_patente`,
                    ),
                  )}
                  refreshDocuments={refreshDocuments}
                  saveAmlData={saveAmlData}
                  hasError={errors.includes("document.upload")}
                />
              </Grid>
              <Grid item xs={11} md={6} lg={4} />
              <Grid item xs={11} md={6} lg={4} />
            </>
          ) : (
            <Grid item xs={11} md={6} lg={4} sx={pt3}>
              <DocumentUpload
                languange={languange}
                label={t("Upload document", "Carica documento")}
                noGrid
                isIdentityDocument={true}
                documentSide={t(
                  `beneficial_owner_${idx}_passport`,
                  `titolare_${idx}_passaporto`,
                )}
                existingDocument={findDocument(
                  t(
                    `beneficial_owner_${idx}_passport`,
                    `titolare_${idx}_passaporto`,
                  ),
                )}
                refreshDocuments={refreshDocuments}
                saveAmlData={saveAmlData}
                hasError={errors.includes("document.upload")}
              />
            </Grid>
          )
        ) : (
          <Grid item xs={11} md={6} lg={4} sx={pt3} />
        )}

        <Grid xs={11} md={12} item>
          <Divider className="divider" />
        </Grid>

        {/* ── RAGIONE TITOLARE ── */}
        <Grid xs={11} md={12} item container>
          <Grid item xs={11} className="container-title-sezione-step">
            <div style={{ display: "flex", alignItems: "center" }}>
              <Typography className="color-blue-dark title-sezione-step">
                {t(
                  "Reason why this person qualifies as UBO of the client",
                  "Ragione cui il soggetto si qualifica quale titolare effettivo",
                )}
              </Typography>
              <div
                className="icon-modal-info-helper"
                style={{
                  marginLeft: "10px",
                  cursor: "pointer",
                  flexShrink: 0,
                  marginTop: "4px",
                }}
                onClick={() =>
                  openModal(
                    t(
                      tooltipContent.ragioneTitolareEng,
                      tooltipContent.ragioneTitolareIta,
                    ),
                  )
                }
              />
            </div>
          </Grid>
        </Grid>

        <Grid item xs={11} md={6} lg={4}>
          <TextField {...field("qualification_reason")} variant="outlined" />
        </Grid>
        <Grid item xs={11} md={6} lg={4} sx={pt1} />
        <Grid item xs={11} md={6} lg={4} sx={pt2} />
        <Grid xs={11} md={12} item>
          <Divider className="divider" />
        </Grid>

        {/* ── PEP ── */}
        <Grid xs={11} md={12} item container>
          <Grid item xs={11} className="container-title-sezione-step">
            <Typography className="color-blue-dark title-sezione-step">
              {t(
                "Politically Exposed Person",
                "Persona politicamente esposta",
              )}
            </Typography>
          </Grid>
          <Grid item xs={11.5} md={10} lg={8}>
            <Box
              className="bg-azure-light"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                borderRadius: "8px",
                padding: "16px 20px",
                marginBottom: "16px",
              }}
            >
              <div
                className="icon-modal-info-helper"
                style={{ flexShrink: 0 }}
              />
              <Typography style={{ fontSize: "14px", lineHeight: 1.6 }}>
                {t(
                  "As a rough guide, a PEP is a person who holds or has held significant public office, including family members and associates.",
                  "A livello indicativo, la PEP è una persona che esercita o ha esercitato funzioni pubbliche di rilievo, inclusi i familiari e i soggetti a essa collegate.",
                )}{" "}
                <span
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                  onClick={() =>
                    openModal(
                      t(
                        tooltipContent.politicamenteEspostoEng,
                        tooltipContent.politicamenteEspostoIta,
                      ),
                    )
                  }
                >
                  {t("Learn more", "Approfondisci")}
                </span>
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={11} md={6} lg={4}>
          <Typography className="color-blue-dark title-input">
            {t(
              "UBO politically exposed person (PEP)?",
              "Titolare effettivo persona politicamente esposta (PEP)?",
            )}
          </Typography>
          <Select
            name="is_pep"
            value={
              data.is_pep === "" || data.is_pep == null
                ? ""
                : String(data.is_pep)
            }
            error={errors.includes("is_pep")}
            onChange={(e) =>
              setData((prev) => ({
                ...prev,
                is_pep:
                  e.target.value === "true"
                    ? true
                    : e.target.value === "false"
                      ? false
                      : "",
              }))
            }
            fullWidth
            displayEmpty
          >
            <MenuItem value="">
              <span style={{ color: "#abb0b6" }}>Seleziona</span>
            </MenuItem>
            {PoliticallyExposed.map((item, i) => (
              <MenuItem key={i} value={String(item.key)}>
                {t(item.labelEng, item.labelIta)}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {showPepQualita ? (
          <>
            <Grid item xs={11} md={6} lg={4} sx={pt1}>
              <Typography className="color-blue-dark title-input">
                {t("PEP type", "Tipo di PEP")}
              </Typography>
              <TextField {...field("pep_role")} variant="outlined" />
            </Grid>
            <Grid item xs={11} md={6} lg={4} sx={pt2} />
          </>
        ) : (
          <>
            <Grid item xs={11} md={6} lg={4} sx={pt1} />
            <Grid item xs={11} md={6} lg={4} sx={pt2} />
          </>
        )}
      </Grid>

      <Modal open={modalOpen} onClose={closeModal}>
        <div>
          <ModalHelper
            onClose={closeModal}
            modalObjectInfo={modalObjectInfo}
            fullWidth={true}
          />
        </div>
      </Modal>
    </>
  );
};

export default TitolareAmlGiuridico;
